import React, { ReactNode, useMemo } from 'react';
import { Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import { useQuery } from '@apollo/client';
import makeStyles from '@mui/styles/makeStyles';
import Description from '@mui/icons-material/Description';
import moment from 'moment';
import {
  EsignatureEventNotificationEntityType,
  EsignatureEventNotificationStatus,
} from '@trnsact/trnsact-shared-types';
import { ContentViewType, ModalsKeys } from '../../global';
import { DateFormats } from '../../constants/common';
import { useModal } from '../../hooks/useModal';
import { useNotifications } from '../../modules/notification';
import Table from '../Table/Table';
import { EntityMobileCards } from '../shared/EntityMobileCards';
import { ListEmpty } from '../shared/List';
import { QUERY_IS_ACCOUNT_HAS_ISVs } from '../../queries/common';
import { ESIGNATURE_EVENT_NOTIFICATIONS } from './queries';
import { ManageESignatureProps, EnvelopeNotificationSummary } from './types';

const ManageESignature: React.FC<ManageESignatureProps> = ({
  vendorOpportunityId,
  accountId,
  creditSubmissionDynamicsId,
  contentViewType = ContentViewType.Desktop,
}) => {
  const classes = useStyles();
  const { handleOpen: handleOpenDocuSignDialog } = useModal(ModalsKeys.DocuSignDialog);
  const { showNotification } = useNotifications();

  const { data } = useQuery(QUERY_IS_ACCOUNT_HAS_ISVs, {
    variables: {
      accountId,
      integrationName: 'DOCUSIGN',
    },
    onError() {
      showNotification('Failed to check integration settings', { type: 'error' });
    },
  });

  const isDocusignAvailable = data?.hasAccountIntegrationSettingValue?.result;

  const { data: eSignatureData, loading: eSignatureLoading } = useQuery(ESIGNATURE_EVENT_NOTIFICATIONS, {
    variables: {
      entityType: EsignatureEventNotificationEntityType.VendorOpportunity,
      entityId: vendorOpportunityId,
    },
    skip: !isDocusignAvailable || !vendorOpportunityId,
    onError() {
      showNotification('Failed to load eSignature information', { type: 'error' });
    },
  });

  // Helper function to get status chip colors
  const getStatusChipColor = (status: string) => {
    // Transitional statuses - part of happy path but not final
    const transitionalStatuses = [EsignatureEventNotificationStatus.Sent, EsignatureEventNotificationStatus.Delivered];
    // Final success status
    const successStatuses = [EsignatureEventNotificationStatus.Completed];
    // Abnormal/error statuses
    const abnormalStatuses = [EsignatureEventNotificationStatus.Declined, EsignatureEventNotificationStatus.Voided];

    if (transitionalStatuses.includes(status as EsignatureEventNotificationStatus)) {
      // Orange/amber for in-progress statuses
      return {
        backgroundColor: '#fff3e0', // Material-UI orange[50]
        color: '#e65100', // Material-UI orange[900]
      };
    } else if (successStatuses.includes(status as EsignatureEventNotificationStatus)) {
      // Green for completed status
      return {
        backgroundColor: '#e8f5e9',
        color: '#2e7d32',
      };
    } else if (abnormalStatuses.includes(status as EsignatureEventNotificationStatus)) {
      // Red for declined/voided statuses
      return {
        backgroundColor: '#ffebee',
        color: '#c62828',
      };
    }
    // Default fallback
    return {
      backgroundColor: '#f5f5f5',
      color: '#616161',
    };
  };

  // Group notifications by envelopeId and process them
  // Note: events are already sorted chronologically (most recent first)
  const envelopeNotificationsSummaries: EnvelopeNotificationSummary[] = useMemo(() => {
    const notifications = eSignatureData?.esignatureEventNotifications || [];
    const envelopeMap = new Map<string, any>();
    const sentDateMap = new Map<string, string>();

    // Process events in order (most recent first)
    notifications.forEach((event: any) => {
      // Store the first (most recent) event for each envelope
      if (!envelopeMap.has(event.envelopeId)) {
        envelopeMap.set(event.envelopeId, event);
      }

      // Store the SENT event date for each envelope
      if (event.status === EsignatureEventNotificationStatus.Sent && !sentDateMap.has(event.envelopeId)) {
        sentDateMap.set(event.envelopeId, event.statusChangedDateTime);
      }
    });

    // Convert to array of processed envelope objects
    return Array.from(envelopeMap.values()).map(latestEvent => ({
      envelopeId: latestEvent.envelopeId,
      templateName: latestEvent.templateName,
      sentDateTime: sentDateMap.get(latestEvent.envelopeId) || null,
      status: latestEvent.status,
      statusChangedDateTime: latestEvent.statusChangedDateTime,
    }));
  }, [eSignatureData?.esignatureEventNotifications]);

  // eSignature table structure
  const eSignatureTableHead = ['Template Name', 'Sent', 'Status'];
  const eSignatureTableData = envelopeNotificationsSummaries.map((item: EnvelopeNotificationSummary) => {
    const chipColors = getStatusChipColor(item.status);
    return [
      item.templateName,
      item.sentDateTime ? moment(item.sentDateTime).format(DateFormats.ShortWithSeconds) : '',
      <Chip
        label={item.status}
        size="small"
        sx={{
          backgroundColor: chipColors.backgroundColor,
          color: chipColors.color,
          fontWeight: 500,
        }}
      />,
    ];
  });

  const renderESignatureByViewType: Record<ContentViewType, ReactNode> = {
    [ContentViewType.Mobile]:
      envelopeNotificationsSummaries.length > 0 ? (
        <EntityMobileCards<any, any>
          entities={envelopeNotificationsSummaries}
          entityAdapter={(entity: EnvelopeNotificationSummary) => {
            const chipColors = getStatusChipColor(entity.status);
            return {
              id: entity.envelopeId,
              title: entity.templateName,
              fields: [
                {
                  label: 'Sent',
                  value: entity.sentDateTime ? moment(entity.sentDateTime).format(DateFormats.ShortWithSeconds) : '',
                },
                {
                  label: 'Status',
                  value: (
                    <Chip
                      label={entity.status}
                      size="small"
                      sx={{
                        backgroundColor: chipColors.backgroundColor,
                        color: chipColors.color,
                        fontWeight: 500,
                      }}
                    />
                  ),
                },
              ],
            };
          }}
          actionsPanel={() => null}
        />
      ) : (
        <ListEmpty />
      ),
    [ContentViewType.Desktop]:
      envelopeNotificationsSummaries.length > 0 ? (
        <Table tableHead={eSignatureTableHead} tableData={eSignatureTableData} hover />
      ) : (
        <ListEmpty />
      ),
  };

  if (!isDocusignAvailable) {
    return null;
  }

  if (eSignatureLoading) {
    return (
      <Box mb="2rem">
        <Box className={classes.subsectionHeader}>
          <Typography variant="h6" className={classes.subsectionTitle}>
            Manage eSignature
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<Description />}
            onClick={() => {
              handleOpenDocuSignDialog({
                link: null,
                vendorOpportunityId,
                creditSubmissionDynamicsId,
              });
            }}
          >
            SEND FOR SIGNATURE
          </Button>
        </Box>
        <Box className={classes.wrapperLoader}>
          <CircularProgress className="progressLoader" size={48} />
          <div className={classes.headerContainer}>
            <Typography variant="body1">Loading eSignature information...</Typography>
          </div>
        </Box>
      </Box>
    );
  }

  return (
    <Box mb="2rem">
      <Box className={classes.subsectionHeader}>
        <Typography variant="h6" className={classes.subsectionTitle}>
          Manage eSignature
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<Description />}
          onClick={() => {
            handleOpenDocuSignDialog({
              link: null,
              vendorOpportunityId,
              creditSubmissionDynamicsId,
            });
          }}
        >
          SEND FOR SIGNATURE
        </Button>
      </Box>
      {renderESignatureByViewType[contentViewType]}
    </Box>
  );
};

export default ManageESignature;

const useStyles = makeStyles(() => ({
  subsectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    paddingBottom: '0.5rem',
  },
  subsectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#424242',
    textTransform: 'none !important' as any,
  },
  wrapperLoader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '64px 0',
    '& .progressLoader': {
      margin: '32px',
    },
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '16px',
  },
}));
